// src/hooks/useProductSearchForBulkOrder.js
import { useState, useCallback, useRef } from 'react';
import { searchProducts } from '../services/api/productSearch';

const useProductSearchForBulkOrder = (shopId, onProductSelect) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showResults, setShowResults] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  const searchTimeoutRef = useRef(null);
  
  // Debounced search function
  const performSearch = useCallback(async (term, currentShopId) => {
    if (!term.trim() || !currentShopId) {
      setSearchResults([]);
      setShowResults(false);
      setHasSearched(false);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await searchProducts(currentShopId, term.trim(), {
        limit: 10,
        page: 1
      });
      
      setSearchResults(response.products || []);
      setShowResults(true);
      setHasSearched(true);
      setSelectedIndex(-1);
    } catch (error) {
      console.error('Product search error:', error);
      setSearchResults([]);
      setShowResults(true);
      setHasSearched(true);
    } finally {
      setIsSearching(false);
    }
  }, []);
  
  // Handle search input change
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value, shopId);
    }, 300);
  }, [shopId, performSearch]);
  
  // Handle keyboard navigation
  const handleKeyDown = useCallback((event) => {
    if (!showResults || searchResults.length === 0) return;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : prev
        );
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
        
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < searchResults.length) {
          selectProduct(searchResults[selectedIndex]);
        }
        break;
        
      case 'Escape':
        event.preventDefault();
        closeDropdown();
        break;
        
      default:
        break;
    }
  }, [showResults, searchResults, selectedIndex]);
  
  // Handle product selection
  const selectProduct = useCallback((product) => {
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
    setIsOpen(false);
    setSelectedIndex(-1);
    setHasSearched(false);
    
    if (onProductSelect) {
      onProductSelect(product);
    }
  }, [onProductSelect]);
  
  // Clear search
  const clearSearch = useCallback(() => {
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
    setIsOpen(false);
    setSelectedIndex(-1);
    setHasSearched(false);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);
  
  // Close dropdown
  const closeDropdown = useCallback(() => {
    setShowResults(false);
    setIsOpen(false);
    setSelectedIndex(-1);
  }, []);
  
  // Open dropdown
  const openDropdown = useCallback(() => {
    setIsOpen(true);
    if (hasSearched) {
      setShowResults(true);
    }
  }, [hasSearched]);
  
  // Format product for display
  const formatProductForDisplay = useCallback((product) => {
    const stockHidden = product.stockQuantity === null;
    return {
      ...product,
      formattedName: product.displayName,
      formattedPrice: `Rs. ${product.price.toFixed(2)}`,
      formattedStock: stockHidden ? null : (product.stockQuantity > 0 ? `${product.stockQuantity} in stock` : 'Out of stock'),
      isOutOfStock: stockHidden ? false : product.stockQuantity <= 0,
      stockHidden
    };
  }, []);
  
  // Format search results for display
  const formattedResults = searchResults.map(formatProductForDisplay);
  
  return {
    // State
    searchTerm,
    searchResults: formattedResults,
    isSearching,
    selectedIndex,
    showResults,
    hasSearched,
    isOpen,
    
    // Handlers
    handleSearchChange,
    handleKeyDown,
    selectProduct,
    clearSearch,
    closeDropdown,
    openDropdown,
    
    // Utilities
    formatProductForDisplay
  };
};

export default useProductSearchForBulkOrder;